import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search, Plane, BookOpenCheck, Layers, FileText, RefreshCw, BedDouble } from "lucide-react";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOURCE_TYPE_META = {
  flight_booking: { label: "Flight",        icon: Plane,         cls: "bg-blue-100 text-blue-700" },
  visa_quote:     { label: "Visa",          icon: BookOpenCheck, cls: "bg-violet-100 text-violet-700" },
  service_entry:  { label: "Service",       icon: Layers,        cls: "bg-cyan-100 text-cyan-700" },
  hotel_booking:  { label: "Hotel",         icon: BedDouble,     cls: "bg-amber-100 text-amber-700" },
  manual:         { label: "Manual",        icon: FileText,      cls: "bg-slate-100 text-slate-600" },
  other:          { label: "Other",         icon: FileText,      cls: "bg-slate-100 text-slate-600" },
};

const INVOICE_STATUS_META = {
  draft:           { label: "Draft",          cls: "bg-slate-100 text-slate-600" },
  sent:            { label: "Sent",           cls: "bg-blue-100 text-blue-700" },
  partially_paid:  { label: "Partially Paid", cls: "bg-amber-100 text-amber-700" },
  paid:            { label: "Paid",           cls: "bg-green-100 text-green-700" },
  overdue:         { label: "Overdue",        cls: "bg-red-100 text-red-700" },
  cancelled:       { label: "Cancelled",      cls: "bg-slate-100 text-slate-400 line-through" },
};

const PAYMENT_STATUS_META = {
  not_requested:   { label: "Not Requested",  cls: "bg-slate-100 text-slate-500" },
  payment_pending: { label: "Payment Pending", cls: "bg-blue-100 text-blue-700" },
  partially_paid:  { label: "Partially Paid", cls: "bg-amber-100 text-amber-700" },
  paid:            { label: "Paid",           cls: "bg-green-100 text-green-700" },
  refunded:        { label: "Refunded",       cls: "bg-purple-100 text-purple-700" },
};

const CURRENCY_SYMBOLS = { EUR: "€", GBP: "£", USD: "$", AED: "د.إ" };

function fmt(amount, currency = "EUR") {
  const sym = CURRENCY_SYMBOLS[currency] || currency;
  return `${sym}${Number(amount || 0).toFixed(2)}`;
}

function StatusBadge({ value, meta }) {
  const m = meta[value] || { label: value, cls: "bg-slate-100 text-slate-600" };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${m.cls}`}>{m.label}</span>;
}

export default function InvoiceList() {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterSource, setFilterSource] = useState("all");
  const [filterInvoiceStatus, setFilterInvoiceStatus] = useState("all");
  const [filterPaymentStatus, setFilterPaymentStatus] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterSource !== "all") params.set("source_type", filterSource);
      if (filterInvoiceStatus !== "all") params.set("invoice_status", filterInvoiceStatus);
      if (filterPaymentStatus !== "all") params.set("payment_status", filterPaymentStatus);
      const res = await axios.get(`${API}/invoices?${params}`, { headers: getAuthHeaders() });
      setInvoices(res.data || []);
    } catch (e) {
      toast.error("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  }, [filterSource, filterInvoiceStatus, filterPaymentStatus]);

  useEffect(() => { load(); }, [load]);

  const filtered = invoices.filter(inv => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (inv.invoice_number || "").toLowerCase().includes(q) ||
      (inv.customer_name || "").toLowerCase().includes(q) ||
      (inv.quote_reference || "").toLowerCase().includes(q)
    );
  });

  const totalInvoiced = filtered.reduce((s, inv) => s + (Number(inv.invoice_total) || 0), 0);
  const totalReceived = filtered.reduce((s, inv) => s + (Number(inv.amount_received) || 0), 0);
  const totalBalance  = filtered.reduce((s, inv) => s + (Number(inv.balance_due) || 0), 0);

  return (
    <DashboardLayout>
      <div className="p-6 max-w-7xl mx-auto">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-serif text-2xl font-bold text-slate-900">Invoices</h1>
            <p className="text-sm text-slate-500 mt-0.5">{filtered.length} invoice{filtered.length !== 1 ? "s" : ""}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button size="sm" className="bg-purple-700 hover:bg-purple-800 text-white"
              onClick={() => navigate("/invoices/new")}>
              <Plus className="w-4 h-4 mr-2" />New Invoice
            </Button>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-3 gap-4 mb-5">
          {[
            { label: "Total Invoiced", value: fmt(totalInvoiced), cls: "text-slate-700" },
            { label: "Total Received", value: fmt(totalReceived), cls: "text-green-700" },
            { label: "Balance Due",    value: fmt(totalBalance),  cls: totalBalance > 0 ? "text-amber-700" : "text-green-700" },
          ].map(c => (
            <div key={c.label} className="bg-white border border-slate-200 rounded-xl p-4">
              <p className="text-xs text-slate-400 uppercase tracking-widest">{c.label}</p>
              <p className={`text-xl font-bold font-mono mt-1 ${c.cls}`}>{c.value}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-4">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search invoice #, customer, ref…"
              className="pl-9 h-9 text-sm" />
          </div>
          <Select value={filterSource} onValueChange={setFilterSource}>
            <SelectTrigger className="h-9 text-sm w-36"><SelectValue placeholder="Source" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources</SelectItem>
              <SelectItem value="flight_booking">Flight</SelectItem>
              <SelectItem value="visa_quote">Visa</SelectItem>
              <SelectItem value="service_entry">Service</SelectItem>
              <SelectItem value="hotel_booking">Hotel</SelectItem>
              <SelectItem value="manual">Manual</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterInvoiceStatus} onValueChange={setFilterInvoiceStatus}>
            <SelectTrigger className="h-9 text-sm w-36"><SelectValue placeholder="Invoice Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {Object.entries(INVOICE_STATUS_META).map(([v, m]) => (
                <SelectItem key={v} value={v}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterPaymentStatus} onValueChange={setFilterPaymentStatus}>
            <SelectTrigger className="h-9 text-sm w-40"><SelectValue placeholder="Payment Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Payments</SelectItem>
              {Object.entries(PAYMENT_STATUS_META).map(([v, m]) => (
                <SelectItem key={v} value={v}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-slate-400">
              <FileText className="w-10 h-10 mb-3 opacity-30" />
              <p className="text-sm">No invoices found</p>
              <p className="text-xs mt-1">Create your first invoice or generate one from a visa quote or booking</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50">
                    {["Invoice #", "Customer", "Source", "Ref", "Date", "Total", "Balance Due", "Invoice Status", "Payment Status"].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((inv, i) => {
                    const srcMeta = SOURCE_TYPE_META[inv.source_type] || SOURCE_TYPE_META.manual;
                    const SrcIcon = srcMeta.icon;
                    return (
                      <tr key={inv.id}
                        className={`border-b border-slate-50 hover:bg-purple-50/40 cursor-pointer transition-colors ${i % 2 === 1 ? "bg-slate-50/30" : ""}`}
                        onClick={() => navigate(`/invoices/${inv.id}`)}>
                        <td className="px-4 py-3 font-mono text-xs font-bold text-purple-700 whitespace-nowrap">
                          {inv.invoice_number}
                        </td>
                        <td className="px-4 py-3 font-medium text-slate-800 max-w-[150px] truncate">
                          {inv.customer_name}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${srcMeta.cls}`}>
                            <SrcIcon className="w-3 h-3" />
                            {srcMeta.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-slate-500 whitespace-nowrap">
                          {inv.quote_reference || "—"}
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">
                          {inv.invoice_date ? format(new Date(inv.invoice_date), "d MMM yyyy") : "—"}
                        </td>
                        <td className="px-4 py-3 font-mono text-sm font-semibold text-slate-700 whitespace-nowrap">
                          {fmt(inv.invoice_total, inv.currency)}
                        </td>
                        <td className={`px-4 py-3 font-mono text-sm font-semibold whitespace-nowrap ${Number(inv.balance_due) > 0 ? "text-amber-700" : "text-green-700"}`}>
                          {fmt(inv.balance_due, inv.currency)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <StatusBadge value={inv.invoice_status} meta={INVOICE_STATUS_META} />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <StatusBadge value={inv.payment_status} meta={PAYMENT_STATUS_META} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>
    </DashboardLayout>
  );
}
